import { useEffect, useRef } from "react";
import type {
  FieldValues,
  Path,
  PathValue,
  UseFormReturn,
} from "react-hook-form";
import {
  calculateMonthlyAmount,
  calculateTsuboUnitPrice,
} from "@ippon/shared-utils";

interface UseTsuboLinkedAmountsOptions<T extends FieldValues> {
  form: UseFormReturn<T>;
  areaTsubo?: number;
  monthlyField: Path<T>;
  unitPriceField: Path<T>;
  enabled?: boolean;
}

const toNumber = (value: unknown): number | undefined => {
  if (value === "" || value === null || value === undefined) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const useTsuboLinkedAmounts = <T extends FieldValues>({
  form,
  areaTsubo,
  monthlyField,
  unitPriceField,
  enabled = true,
}: UseTsuboLinkedAmountsOptions<T>): void => {
  const monthlyValue = form.watch(monthlyField);
  const unitPriceValue = form.watch(unitPriceField);
  const previous = useRef({ monthlyValue, unitPriceValue });
  const automaticallyUpdatedField = useRef<Path<T> | null>(null);

  useEffect(() => {
    const monthlyChanged = previous.current.monthlyValue !== monthlyValue;
    const unitPriceChanged = previous.current.unitPriceValue !== unitPriceValue;
    previous.current = { monthlyValue, unitPriceValue };

    if (!enabled || !areaTsubo || areaTsubo <= 0) return;

    // 既存契約は月額だけが保存されている場合がある。フォーム初期化や坪数取得後に
    // 坪単価が空なら、ユーザー操作を待たず月額から表示値を補完する。
    if (
      toNumber(monthlyValue) !== undefined &&
      toNumber(unitPriceValue) === undefined
    ) {
      const nextUnitPrice = calculateTsuboUnitPrice(
        toNumber(monthlyValue),
        areaTsubo,
      );
      automaticallyUpdatedField.current = unitPriceField;
      form.setValue(
        unitPriceField,
        String(nextUnitPrice ?? "") as PathValue<T, Path<T>>,
        { shouldDirty: false, shouldValidate: true },
      );
      return;
    }

    // 坪単価だけ保存されているデータも同様に月額を補完する。
    if (
      toNumber(unitPriceValue) !== undefined &&
      toNumber(monthlyValue) === undefined
    ) {
      const nextMonthly = calculateMonthlyAmount(
        toNumber(unitPriceValue),
        areaTsubo,
      );
      automaticallyUpdatedField.current = monthlyField;
      form.setValue(
        monthlyField,
        String(nextMonthly ?? "") as PathValue<T, Path<T>>,
        { shouldDirty: false, shouldValidate: true },
      );
      return;
    }

    if (
      (automaticallyUpdatedField.current === monthlyField && monthlyChanged) ||
      (automaticallyUpdatedField.current === unitPriceField && unitPriceChanged)
    ) {
      automaticallyUpdatedField.current = null;
      return;
    }

    if (unitPriceChanged && form.getFieldState(unitPriceField).isDirty) {
      const nextMonthly = calculateMonthlyAmount(
        toNumber(unitPriceValue),
        areaTsubo,
      );
      if (String(nextMonthly ?? "") === String(monthlyValue ?? "")) return;
      automaticallyUpdatedField.current = monthlyField;
      form.setValue(
        monthlyField,
        String(nextMonthly ?? "") as PathValue<T, Path<T>>,
        { shouldDirty: true, shouldValidate: true },
      );
      return;
    }

    if (monthlyChanged && form.getFieldState(monthlyField).isDirty) {
      const nextUnitPrice = calculateTsuboUnitPrice(
        toNumber(monthlyValue),
        areaTsubo,
      );
      if (String(nextUnitPrice ?? "") === String(unitPriceValue ?? "")) return;
      automaticallyUpdatedField.current = unitPriceField;
      form.setValue(
        unitPriceField,
        String(nextUnitPrice ?? "") as PathValue<T, Path<T>>,
        { shouldDirty: true, shouldValidate: true },
      );
    }
  }, [
    areaTsubo,
    enabled,
    form,
    monthlyField,
    monthlyValue,
    unitPriceField,
    unitPriceValue,
  ]);
};
